import { useEffect, useId, useState } from 'react'

const TITLES = {
  in: '입고',
  out: '출고',
  adjust: '재고조정',
}

export default function QuickStockPanel({
  open,
  type,
  part,
  initialQty = 1,
  onClose,
  onSubmit,
}) {
  const titleId = useId()
  const [qty, setQty] = useState(initialQty)
  const [target, setTarget] = useState(part?.quantity ?? 0)
  const [memo, setMemo] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setQty(initialQty)
    setTarget(part?.quantity ?? 0)
    setMemo('')
    setError('')
  }, [open, initialQty, part])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || !part) return null

  const title = TITLES[type] || '처리'
  const isOut = type === 'out'
  const isAdjust = type === 'adjust'

  async function handleSubmit(event) {
    event.preventDefault()
    if (isAdjust) {
      const next = Number(target)
      if (!Number.isInteger(next) || next < 0) {
        setError('0 이상의 실제 수량을 입력해 주세요.')
        return
      }
      if (next === Number(part.quantity)) {
        setError('현재 수량과 동일합니다.')
        return
      }
    } else {
      const quantity = Number(qty)
      if (!Number.isInteger(quantity) || quantity <= 0) {
        setError('1 이상의 수량을 입력해 주세요.')
        return
      }
      if (isOut && quantity > part.quantity) {
        setError(`현재 재고가 부족합니다. 현재 재고: ${part.quantity}${part.unit || '개'}`)
        return
      }
    }

    setSaving(true)
    setError('')
    try {
      await onSubmit({
        type,
        quantity: Number(qty),
        targetQuantity: Number(target),
        memo: memo.trim(),
      })
    } catch (err) {
      setError(err.message || '권한이 없습니다.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <form
        className="modal-panel"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="modal-title">
          {title}
        </h2>
        <p className="modal-model">{part.model || part.name}</p>
        <p className="modal-stock">
          현재재고 <strong>{part.quantity}</strong> {part.unit}
        </p>

        {isAdjust ? (
          <label className="field">
            <span>실제수량</span>
            <input
              className="input input--qty"
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
            />
          </label>
        ) : (
          <label className="field">
            <span>수량</span>
            <div className="qty-stepper">
              <button
                type="button"
                className="btn btn--ghost btn--touch"
                onClick={() => setQty((n) => Math.max(1, Number(n) - 1))}
                aria-label="수량 감소"
              >
                −
              </button>
              <input
                className="input input--qty"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={qty}
                onChange={(event) => setQty(event.target.value)}
              />
              <button
                type="button"
                className="btn btn--ghost btn--touch"
                onClick={() => setQty((n) => Number(n) + 1)}
                aria-label="수량 증가"
              >
                +
              </button>
            </div>
          </label>
        )}

        <label className="field">
          <span>{isAdjust ? '사유 / 메모' : '메모'}</span>
          <input
            className="input"
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
            placeholder={isAdjust ? '실사, 파손 등' : '선택 입력'}
          />
        </label>

        {error ? <p className="form-error">{error}</p> : null}

        <div className="modal-actions">
          <button type="button" className="btn btn--ghost btn--touch" onClick={onClose}>
            취소
          </button>
          <button
            type="submit"
            className={`btn btn--touch ${isOut ? 'btn--danger' : 'btn--primary'}`}
            disabled={saving}
          >
            {saving ? '처리 중...' : `${title} 처리`}
          </button>
        </div>
      </form>
    </div>
  )
}
